import { useInstance } from '@meraki-internal/react-dependency-injection';
import { OrganizationsState } from './OrganizationsState';
import { useSubscription } from '@meraki-internal/state';
import { IonButton, IonItem, IonLabel, IonList, IonSkeletonText, IonText } from '@ionic/react';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { useState } from 'react';
import { IOrganizationUser } from './IOrganization';
import { Text } from '../components/Text';
import { AddClient } from './AddClient';
import { UserState } from '../user/UserState';
import { createUseStyles } from 'react-jss';
import { Menu } from '../components/Menu';
import { Card } from '../components/Card';
import { DeleteIcon } from '../theme/Icons';
import { ILightCareMap } from '../caremap/ICareMap';
import { CareMapState } from '../caremap/CareMapState';
import { useAsyncEffect } from '../support/useAsyncEffect';
import { AppPrincipal } from '../auth/AppPrincipal';
import { AlertPresenter } from '../AlertBinder';
import { OrganizationCareMapMenuItem } from './OrganizationCareMapMenuItem';
import { ClientAccessTip } from './ClientAccessTip';
import { MemberAvatar } from '../household/MemberAvatar';
import { OrganizationInsightsState } from './OrganizationInsightsState';
import { CaseloadSearchSection } from '../caseload-search/CaseloadSearchSection';
import { CaseloadSearchState } from '../caseload-search/CaseloadSearchState';
import { buildNameQueryTokens, matchesNameQuery } from '../caseload-search/clientNameMatch';

const useStyles = createUseStyles({
    // matches the search card's wrapper so the two cards sit the same distance apart and align
    section: {
        display: 'flow-root',
        marginBottom: 10
    },

    // Same weight and padding as the search card's header, so the two cards read as a pair rather
    // than as a card and a page heading that happen to be stacked.
    rosterHead: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '14px 16px 10px'
    },

    rosterCount: {
        marginTop: 1,
        fontSize: 12.5,
        color: 'var(--ion-color-step-600)'
    },

    filterBanner: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 4,
        margin: '0 10px 10px 10px',
        padding: '6px 4px 6px 12px',
        borderRadius: 8,
        backgroundColor: 'var(--ion-color-step-50)'
    },

    clientList: {
        '& ion-item': {
            '--inner-padding-end': '12px'
        }
    },

    clientRow: {
        display: 'flex !important',
        alignItems: 'start',
        justifyContent: 'space-between'
    },

    clientIdentity: {
        flex: 1,
        minWidth: 0
    },

    // a client whose care recipient profile does not exist yet has no care map row to lead with,
    // so fall back to the account holder; spacing mirrors the care map row's avatar
    accountOnlyIdentity: {
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 16,
        paddingTop: 12,
        paddingBottom: 12,
        whiteSpace: 'normal'
    },

    accountOnlyAvatar: {
        marginInlineEnd: 18
    },

    name: {
        fontWeight: 500
    },

    detailLine: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1.6,
        color: 'var(--ion-color-step-650)'
    },

});

export const OrganizationClients: React.FC<{ orgId: string; }> = ({ orgId }) => {
    const classes = useStyles();

    const history = useInstance(HistoryViewModel);
    useSubscription(() => history);

    const principal = useInstance(AppPrincipal);

    const orgState = useInstance(OrganizationsState);
    useSubscription(() => orgState);

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const alertPresenter = useInstance(AlertPresenter);

    const insightsState = useInstance(OrganizationInsightsState);
    useSubscription(() => insightsState);

    const searchState = useInstance(CaseloadSearchState);
    useSubscription(() => searchState);

    const [careMapsLoaded, setCareMapsLoaded] = useState<boolean>(false);
    const [addClientOpen, setAddClientOpen] = useState<boolean>(false);

    const org = orgState.getOrganization(orgId);
    const allOrgClients = orgState.getOrganizationClientUsers(orgId);

    useAsyncEffect(async () => {
        setCareMapsLoaded(false);
        await orgState.loadOrganizationCareMaps(orgId);
        setCareMapsLoaded(true);
    }, [orgId, orgState]);

    // The care maps have to be in hand before their insights can be read, and the roster orders
    // itself by what comes back.
    useAsyncEffect(async () => {
        await insightsState.load(orgId);
    }, [orgId, insightsState]);

    if(!org){
        return null;
    }

    const deleteOrg = async () => {
        alertPresenter.showAlertV2({
            header: `Delete ${org.name}?`,
            message: `${org.name} will only be removed if there are no existing clients. Former clients will still retain access to their own Innerhive data, but ${org.name} will lose access to it.`,
            options: [{
                label: 'Delete',
                id: 'delete',
                role: 'destructive',
                handler: async () => {
                    try {
                        await orgState.deleteOrganization(orgId);
                        history.push('/');
                    }
                    catch (err){
                        alertPresenter.showAndLogError(err);
                    }
                }
            }, {
                label: 'Cancel',
                role: 'cancel',
                id: 'cancel'
            }]
        });
    };

    const closeOrDismissAddClient = () => {
        setAddClientOpen(false);
    };

    const deleteUser = async (user: IOrganizationUser) => {
        const name = `${user.firstName} ${user.lastName}`;
        alertPresenter.showAlertV2({
            header: `Delete ${name}?`,
            message: `${name} will be removed from your organization. They will still retain access to their own Innerhive data, but ${org.name} will lose access to it.`,
            options: [{
                label: 'Delete',
                id: 'delete',
                role: 'destructive',
                handler: async () => {
                    await orgState.removeUser(user);
                }
            }, {
                label: 'Cancel',
                id: 'cancel',
                role: 'cancel'
            }]
        });
    };

    const onCareMapClick = async (map: ILightCareMap) => {
        history.push(`/care/${map.careMapId}`);
    };

    const teamMemberId = history.getCurrentSearchParams().get('user');
    const filteredTeamMember = teamMemberId ? orgState.getOrganizationUser({ orgId, userId: teamMemberId }) : undefined;
    const clients = teamMemberId ? orgState.getClientsCoOwnedByUser({ orgId, teamMemberId }) : allOrgClients;

    // a case manager scans their book by the person they look after, not by the account holder
    const careRecipientName = (user: IOrganizationUser) => {
        const [primaryCareMap] = orgState.getOrganizationCareMapsForUser(orgId, user.userId);
        const patientName = primaryCareMap ? careMapState.getPatientFullName(primaryCareMap) : '';
        return patientName || `${user.firstName} ${user.lastName}`;
    };

    const primaryCareMapId = (user: IOrganizationUser) => {
        const [primaryCareMap] = orgState.getOrganizationCareMapsForUser(orgId, user.userId);
        return primaryCareMap?.careMapId;
    };

    // A caseload is filed by family name, and it is how a case manager asks for someone, so the
    // list sorts on the last name even though it reads first name first.
    const careRecipientSortKey = (user: IOrganizationUser) => {
        const [primaryCareMap] = orgState.getOrganizationCareMapsForUser(orgId, user.userId);
        const patient = primaryCareMap?.patient;
        const hasPatientName = Boolean(patient?.firstName || patient?.lastName);
        const firstName = hasPatientName ? (patient?.firstName || '') : user.firstName;
        const lastName = hasPatientName ? (patient?.lastName || '') : user.lastName;
        return `${lastName} ${firstName}`.trim().toLowerCase();
    };

    // Built once rather than asked per comparison, because answering it means sorting every finding
    // in the caseload and a comparator would do that on each of n log n calls.
    const careMapIdsNeedingAttention = new Set(insightsState.getAttentionItems().map(entry => entry.careMapId));

    // Two sections, not a ranking: clients with something outstanding lead, everyone else follows,
    // and each section is alphabetical by family name. Urgency decides which finding a client leads
    // with, not where they sit in the list, because a caseload is scanned by name and the ranking
    // behind an order is invisible on the row.
    const sortedClients = [...clients].sort((u1, u2) => {
        const careMapId1 = primaryCareMapId(u1);
        const careMapId2 = primaryCareMapId(u2);

        const needsAttention1 = Boolean(careMapId1 && careMapIdsNeedingAttention.has(careMapId1));
        const needsAttention2 = Boolean(careMapId2 && careMapIdsNeedingAttention.has(careMapId2));
        if (needsAttention1 !== needsAttention2) {
            return needsAttention1 ? -1 : 1;
        }

        return careRecipientSortKey(u1).localeCompare(careRecipientSortKey(u2));
    });

    // The caseload search doubles as the roster filter, so a name narrows this list whether it was
    // typed on its own or inside a question. It stacks with the team-member filter rather than
    // replacing it.
    const queryTokens = buildNameQueryTokens(searchState.state.query);
    const nameMatches = sortedClients.filter(user => matchesNameQuery(careRecipientName(user), queryTokens));

    // The list narrows only when the query actually names somebody. Most of what gets typed here is a
    // question about the caseload rather than a name in it, and filtering that down to nothing would
    // take the caseload away mid-sentence and read as though the clients had gone.
    const visibleClients = nameMatches.length > 0 ? nameMatches : sortedClients;

    const clientCount = allOrgClients.length === 1 ? '1 client' : `${allOrgClients.length} clients`;
    const isNarrowed = Boolean(teamMemberId) || nameMatches.length > 0;
    const countHeading = isNarrowed ? `${visibleClients.length} of ${clientCount}` : clientCount;

    return (
        <>


            <CaseloadSearchSection />

            <div className={classes.section}>
            <Card data-type="caseload-section">
            <div className={classes.rosterHead}>
                <div>
                    <Text><b>Caseload</b></Text>
                    <div className={classes.rosterCount} data-type="client-count">{countHeading}</div>
                </div>
                {orgState.canAddOrganizationUser(orgId, 'org-client') && (
                    <IonButton
                        fill="solid"
                        color="primary"
                        data-id="add-org-client"
                        onClick={() => setAddClientOpen(true)}
                    >+ Add Client</IonButton>
                )}
            </div>

            {teamMemberId && (
                <div className={classes.filterBanner} data-id="client-filter-banner">
                    <IonText className={classes.detailLine}>
                        {filteredTeamMember
                            ? <>Showing only the clients held by <span className="mp-mask">{filteredTeamMember.firstName} {filteredTeamMember.lastName}</span>.</>
                            : <>Showing only the clients held by one team member.</>
                        }
                    </IonText>
                    <IonButton
                        size="small"
                        fill="clear"
                        data-id="clear-client-filter"
                        onClick={() => history.removeSearchParam('user')}
                    >Show all clients</IonButton>
                </div>
            )}

            <IonList className={classes.clientList} data-id="org-client-list">
                {visibleClients.map(user => {
                    const careMaps = orgState.getOrganizationCareMapsForUser(org.orgId, user.userId);

                    return (
                        <IonItem key={user.email} data-client-email={user.email}>
                            <IonLabel>
                                <div className={classes.clientRow}>
                                    <div className={classes.clientIdentity}>
                                        <div data-type="org-client-caremap-list">
                                            {careMapsLoaded ? (
                                                careMaps.map(map => (
                                                    <OrganizationCareMapMenuItem
                                                        key={map.careMapId}
                                                        careMap={map}
                                                        onClick={onCareMapClick}
                                                    />
                                                ))
                                            ): (
                                                <div style={{display: 'flex', width: '100%', marginTop: 10, paddingLeft: 16}}>
                                                    <IonSkeletonText animated style={{'--border-radius': '999px', width: 48, height: 48, marginRight: 10}}></IonSkeletonText>
                                                    <div style={{paddingTop: 4}}>
                                                        <IonSkeletonText animated style={{'--border-radius': '999px', width: 200, height: '30%'}}></IonSkeletonText>
                                                        <IonSkeletonText animated style={{'--border-radius': '999px', width: 200, height: '30%', marginTop: 10}}></IonSkeletonText>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {careMapsLoaded && careMaps.length === 0 && (
                                            <div className={classes.accountOnlyIdentity} data-type="client-without-caremap">
                                                <div className={classes.accountOnlyAvatar}>
                                                    <MemberAvatar small member={{}} />
                                                </div>
                                                <div>
                                                    <div className={`${classes.name} mp-mask`}>{user.firstName} {user.lastName}</div>
                                                    <IonText className={classes.detailLine}>No care recipient profile yet</IonText>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {careMapsLoaded && orgState.hasUnreadableCareMap(careMaps) && (
                                        <ClientAccessTip />
                                    )}
                                    <Menu actions={[
                                        // adding a 2nd caremap is an edge case
                                        // 1. we create their "owner" caremap for them when we created the client
                                        // 2. adding a caremap to an org user assumes it is going to be an "owner" caremap
                                        //      and so work remains to support adding a caremap on the client, for someone
                                        //      other than the client
                                        // 3. we might need this when supporting existing users (eg add existing user, but
                                        //      they don't have a owner caremap yet). (once we get their permission)
                                        // {
                                        //     Icon: PlusIcon,
                                        //     text: 'Add Care Map',
                                        //     'data-type': 'add-client-caremap',
                                        //     handler: () => history.push(`/add-caremap/${org.orgId}/${user.userId}`),
                                        //     disabled: !orgState.canAddClientCareMap(user)
                                        // },
                                        {
                                            Icon: DeleteIcon,
                                            text: 'Delete Client',
                                            role: 'destructive',
                                            handler: () => deleteUser(user),
                                            'data-type': 'delete-client',
                                            disabled: !orgState.canRemoveOrganizationUser(user)
                                        }
                                    ]} />
                                </div>
                            </IonLabel>
                        </IonItem>
                    );
                })}
            </IonList>
            </Card>
            </div>

            {principal.isAdmin && (
                <>
                    <br/><br/><br/><br/>
                    <IonButton data-id="delete-org" onClick={deleteOrg}>Delete Org</IonButton>
                </>
            )}

            <AddClient
                orgId={orgId}
                isOpen={addClientOpen}
                closeHandler={closeOrDismissAddClient}
            />
        </>
    );
};
